import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../../../api'
import { cn } from '../../../lib/utils'
import { TrendChart } from '../../charts/TrendChart'
import { BarChart } from '../../charts/BarChart'
import { CHART_COLORS, formatCurrency } from '../../../config/chartTheme'
import { DetailSkeleton } from '../../finance/SkeletonStates'
import { AiCommentary } from '../../ai'

interface Level1MetricDetailProps {
  metricCode: string
  entityCode: string
  periodCode: string
  onDrill: (dimensionType: string, dimensionValue: string, label: string) => void
}

type BreakdownDimension = 'account' | 'entity'

export function Level1MetricDetail({
  metricCode, entityCode, periodCode, onDrill,
}: Level1MetricDetailProps) {
  const [dimension, setDimension] = useState<BreakdownDimension>('account')

  // Trend data
  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['metric-trend', metricCode, entityCode],
    queryFn: () => api.get(`/metrics/financial/trend/${metricCode}`, {
      params: { entity_code: entityCode, periods: 12 },
    }).then(r => r.data),
  })

  // Breakdown data (new endpoint)
  const { data: breakdownData, isLoading: breakdownLoading } = useQuery({
    queryKey: ['metric-breakdown', metricCode, periodCode, entityCode, dimension],
    queryFn: () => api.get(`/metrics/financial/breakdown/${metricCode}`, {
      params: {
        dimension,
        period_code: periodCode || undefined,
        entity_code: entityCode,
      },
    }).then(r => r.data),
    enabled: !!metricCode && !!periodCode,
    staleTime: 5 * 60 * 1000,
  })

  // Variance data (secondary — period-over-period change)
  const { data: varianceData } = useQuery({
    queryKey: ['metric-variance', metricCode, periodCode, entityCode],
    queryFn: () => api.get(`/metrics/variance/${metricCode}`, {
      params: { period_code: periodCode, entity_code: entityCode },
    }).then(r => r.data),
    enabled: !!periodCode,
  })

  const trend = (trendData?.trend || []).map((t: any) => ({
    period: t.period_name || t.period_code,
    value: t.numeric_value ?? 0,
  }))
  const unit = trendData?.trend?.[0]?.unit || 'USD'

  // Build breakdown bars from the new breakdown endpoint
  const items = (breakdownData?.breakdown || []).slice(0, 15)
  const total = breakdownData?.total ?? 0
  const barData = items.map((b: any) => ({
    label: b.label || b.code || 'Unknown',
    value: Math.abs(b.value ?? 0),
    _code: b.code,
    _name: b.label,
  }))

  if (trendLoading) return <DetailSkeleton />

  return (
    <div className="space-y-4">
      {/* Trend Chart */}
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
      )}>
        <div className="flex items-center gap-1.5 mb-3">
          <div className="h-1.5 w-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            {metricCode.replace(/_/g, ' ')} — 12-Period Trend
          </span>
        </div>
        {trend.length > 0 ? (
          <TrendChart data={trend} unit={unit} height={260} showArea color={CHART_COLORS.accent} />
        ) : (
          <div className="text-center py-12 text-[13px] text-[var(--text-muted,#64748B)]">
            No trend data available
          </div>
        )}
      </div>

      {/* Breakdown */}
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
      )}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
            <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
              Breakdown
            </span>
            {total !== 0 && (
              <span className="font-['JetBrains_Mono',monospace] text-[11px] text-[var(--text-secondary,#94A3B8)] ml-2">
                Total: {formatCurrency(total)}
              </span>
            )}
          </div>
          <div className="flex gap-1">
            {(['account', 'entity'] as BreakdownDimension[]).map(v => (
              <button
                key={v}
                onClick={() => setDimension(v)}
                className={cn(
                  'text-[10px] px-2.5 py-1 rounded-md transition-colors capitalize',
                  dimension === v
                    ? 'bg-[var(--accent,var(--accent))]/10 text-[var(--accent,var(--accent))]'
                    : 'text-[var(--text-muted,#64748B)] hover:text-[var(--text-secondary,#94A3B8)]',
                )}
              >
                By {v}
              </button>
            ))}
          </div>
        </div>

        {breakdownLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-8 rounded bg-white/[0.04] animate-pulse" />
            ))}
          </div>
        ) : barData.length > 0 ? (
          <BarChart
            data={barData}
            height={Math.min(450, 40 + barData.length * 36)}
            unit={unit}
            horizontal
            color={CHART_COLORS.series[1]}
            onClick={(entry: any) => {
              const code = entry?._code || entry?.label
              const label = entry?._name || entry?.label || code
              onDrill(dimension, code, label)
            }}
          />
        ) : (
          <div className="text-center py-12 text-[13px] text-[var(--text-muted,#64748B)]">
            No breakdown data available for this period
          </div>
        )}
      </div>

      {/* Variance summary (secondary — shows period change) */}
      {varianceData && varianceData.variance_amount != null && (
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
        )}>
          <div className="flex items-center gap-1.5 mb-2">
            <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
              Period-over-Period Change
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className={cn(
              "font-['JetBrains_Mono',monospace] text-[16px] font-semibold",
              (varianceData.variance_amount ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400',
            )}>
              {varianceData.variance_amount >= 0 ? '+' : ''}{formatCurrency(varianceData.variance_amount)}
            </span>
            {varianceData.variance_pct != null && (
              <span className="text-[12px] text-[var(--text-muted,#64748B)]">
                ({varianceData.variance_pct >= 0 ? '+' : ''}{varianceData.variance_pct?.toFixed(1)}%)
              </span>
            )}
            <span className="text-[11px] text-[var(--text-hint,#475569)]">
              vs prior period
            </span>
          </div>
        </div>
      )}

      {/* AI Commentary */}
      <AiCommentary metricCode={metricCode} />
    </div>
  )
}
